import { LoadingSpinner } from "../../core/components/LoadingSpinner";
import { useMeDetailsQuery } from 'legoland-sdk/dist/experimental';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSortBy, useTable } from 'react-table';
import { Box, Text, useTombac } from 'tombac';
import { ChevronDownIcon, ChevronUpIcon } from 'tombac-icons';
import { useContractListQuery } from "../contractsApiHooks";
import { Pagination } from "./Pagination";
import { Table, Tbody, Td, Th, Thead, Tr } from "./Table";
import { createContractListColumns } from "./createContractListColumns";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function useContractListProps({
  enablePaging,
  enableSortBy
} = {}) {
  const [page, setPage] = useState();
  const [sortBy, setSortBy] = useState();
  const props = {};
  if (enablePaging) {
    props.onPageChange = setPage;
    props.page = page;
  }
  if (enableSortBy) {
    props.onSortByChange = setSortBy;
    props.sortBy = sortBy;
  }
  return props;
}
function isSortByEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}
const defaultSortBy = [];
export const ContractList = ({
  filters,
  onPageChange,
  onSortByChange,
  sortBy = defaultSortBy,
  page,
  variant = 'default'
}) => {
  const tombac = useTombac();
  const meDetailsQuery = useMeDetailsQuery();
  const userRole = meDetailsQuery?.data?.meDetails.role;
  const {
    data,
    error,
    isLoading
  } = useContractListQuery({
    filters,
    sortBy,
    page
  }, {
    keepPreviousData: true
  });
  const tableData = useMemo(() => data?.content ?? [], [data]);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setSortBy,
    state: {
      sortBy: tableSortBy
    }
  } = useTable({
    columns: useMemo(() => createContractListColumns({
      variant,
      userRole
    }), []),
    data: tableData,
    disableMultiSort: true,
    disableSortBy: !onSortByChange,
    initialState: {
      sortBy
    },
    manualSortBy: true
  }, useSortBy);
  const tableSortByRef = useRef(tableSortBy);
  useEffect(function handleSortByPropChange() {
    if (!isSortByEqual(sortBy, tableSortByRef.current)) {
      // condition prevents infinite loop
      setSortBy(sortBy);
    }
  }, [sortBy]);
  useEffect(function handleTableSortByChange() {
    if (!onSortByChange) return;
    onSortByChange(tableSortBy);
    tableSortByRef.current = tableSortBy;
  }, [tableSortBy]);
  if (error && !isLoading) {
    return /*#__PURE__*/_jsx(Box, {
      $display: "flex",
      $justifyContent: "center",
      $mt: "3sp",
      children: /*#__PURE__*/_jsx(Text, {
        variant: "secondary",
        children: "Something went wrong"
      })
    });
  }
  return isLoading ? /*#__PURE__*/_jsx(LoadingSpinner, {}) : /*#__PURE__*/_jsxs(Box, {
    $boxSizing: "content-box",
    $margin: "3sp auto",
    $maxWidth: "1440u",
    $padding: variant === 'narrow' ? '0' : '0 3sp',
    children: [variant !== 'narrow' && /*#__PURE__*/_jsx(Pagination, {
      $paddingBottom: "2sp",
      page: page,
      itemCount: data.totalElements,
      onPageChange: page => onPageChange && onPageChange(page)
    }), tableData.length === 0 ? /*#__PURE__*/_jsx(Box, {
      $display: "flex",
      $justifyContent: "center",
      $mt: "3sp",
      children: /*#__PURE__*/_jsx(Text, {
        variant: "secondary",
        children: Object.values(filters).filter(f => f && (typeof f !== 'object' || !('length' in f) || f.length > 0)).length > 0 ? filters.user ? 'No contracts are assigned to this user' : 'No contract matches the criteria' : 'No contracts yet'
      })
    }) : /*#__PURE__*/_jsxs(Table, {
      ...getTableProps(),
      children: [/*#__PURE__*/_jsx(Thead, {
        children: headerGroups.map(headerGroup => {
          const {
            key: headerGroupKey,
            ...headerGroupProps
          } = headerGroup.getHeaderGroupProps();
          return /*#__PURE__*/_jsx(Tr, {
            ...headerGroupProps,
            children: headerGroup.headers.map(column => {
              const {
                key: headerKey,
                ...headerProps
              } = column.getHeaderProps(column.getSortByToggleProps({
                style: {
                  width: tombac.unit(Number(column.width))
                },
                title: undefined
              }));
              return /*#__PURE__*/_jsxs(Th, {
                ...headerProps,
                sticky: variant === 'default',
                children: [column.render('Header'), ' ', column.isSorted && (column.isSortedDesc ? /*#__PURE__*/_jsx(ChevronDownIcon, {
                  size: "xs"
                }) : /*#__PURE__*/_jsx(ChevronUpIcon, {
                  size: "xs"
                }))]
              }, headerKey);
            })
          }, headerGroupKey);
        })
      }), /*#__PURE__*/_jsx(Tbody, {
        ...getTableBodyProps(),
        children: rows.map(row => {
          prepareRow(row);
          const {
            key: rowKey,
            ...rowProps
          } = row.getRowProps();
          return /*#__PURE__*/_jsx(Tr, {
            ...rowProps,
            $height: "56u",
            children: row.cells.map(cell => {
              const {
                key: cellKey,
                ...cellProps
              } = cell.getCellProps();
              return /*#__PURE__*/_jsx(Td, {
                $overflow: "hidden",
                $whiteSpace: "nowrap",
                ...cellProps,
                ref: ref => {
                  if (!ref) return;
                  ref.title = ref.offsetWidth < ref.scrollWidth && cell.value ? cell.value : '';
                },
                children: cell.render('Cell')
              }, cellKey);
            })
          }, rowKey);
        })
      })]
    })]
  });
};